import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController
} from 'chart.js';
import { format, parseISO } from 'date-fns';
import { getPlayerStatsByCategory } from '../utils/playerUtils';
import { getTeamLogo, normalizeTeamName } from '../utils/teamUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

// Create a custom plugin for team logos
const teamLogoPlugin = {
  id: 'teamLogos',
  afterDatasetsDraw: (chart, args, options) => {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const logos = options.logos || [];
    
    meta.data.forEach((element, index) => {
      const logo = logos[index];
      if (!logo || !logo.complete || logo.naturalWidth === 0) return;
      
      const { x, y } = element.getCenterPoint();
      const logoSize = 20;
      
      ctx.save();
      ctx.drawImage(
        logo,
        x - logoSize / 2,
        y - element.height / 2 - logoSize - 5,
        logoSize,
        logoSize
      );
      ctx.restore();
    });
  }
};

// Create a custom plugin for TOG indicators
const togIndicatorPlugin = {
  id: 'togIndicator',
  afterDatasetsDraw: (chart, args, options) => {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const gameData = options.gameData || [];
    
    meta.data.forEach((element, index) => {
      const game = gameData[index];
      if (!game) return;
      
      const tog = game.timeOnGroundPercentage || 0;
      if (tog < 50) {
        const { x, y } = element.getCenterPoint();
        const bottom = chart.chartArea.bottom;
        
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = '12px Arial';
        ctx.fillText('⏰', x, bottom - 2);
        ctx.restore();
      }
    });
  }
};

// Register the custom plugins
ChartJS.register(teamLogoPlugin, togIndicatorPlugin);

// Team name mapping for logos
const normalizeTeamNameForLogo = (teamName) => {
  const mappings = {
    'broncos': 'Broncos',
    'bulldogs': 'Bulldogs',
    'cowboys': 'Cowboys',
    'dolphins': 'Dolphins',
    'dragons': 'Dragons',
    'eels': 'Eels',
    'knights': 'Knights',
    'panthers': 'Panthers',
    'rabbitohs': 'Rabbitohs',
    'raiders': 'Raiders',
    'roosters': 'Roosters',
    'sea eagles': 'Sea Eagles',
    'sharks': 'Sharks',
    'storm': 'Storm',
    'titans': 'Titans',
    'warriors': 'Warriors',
    'wests tigers': 'Wests Tigers'
  };

  if (!teamName) return '';
  const normalized = teamName.toLowerCase().trim();
  
  // Check direct mapping
  for (const [key, value] of Object.entries(mappings)) {
    if (normalized.includes(key)) {
      return value;
    }
  }
  
  // If no mapping found, return original
  return teamName;
};

// Format round name function
const formatRoundName = (roundName) => {
  if (!roundName) return '';
  return roundName;
};

const StatChart = ({ 
  players, 
  playerId, 
  statCategory, 
  line, 
  onLineChange, 
  gameSpan,
  isDarkMode,
  activeFilters,
  isFilterActive,
  toggleFilter,
  selectedSeasons = [],
  onGamesDisplayedChange
}) => {
  const [showStatBreakdown, setShowStatBreakdown] = React.useState(false);
  const [minsFilter, setMinsFilter] = React.useState(0);
  const [gamesDisplayed, setGamesDisplayed] = React.useState(40);
  const [locationFilter, setLocationFilter] = React.useState('all'); // 'all', 'home', 'away'
  const [resultFilter, setResultFilter] = React.useState('all'); // 'all', 'wins', 'losses'
  
  if (!players || !playerId || !statCategory) {
    return null;
  }

  // Call the parent handler when gamesDisplayed changes
  React.useEffect(() => {
    if (onGamesDisplayedChange) {
      onGamesDisplayedChange(gamesDisplayed);
    }
  }, [gamesDisplayed, onGamesDisplayedChange]);

  // Get unique games for the player
  let sortedStats = React.useMemo(() => {
    const playerGames = players
      .filter(p => p.player_name === playerId)
      // Apply season filter
      .filter(p => selectedSeasons.length === 0 || selectedSeasons.includes(p.year))
      // Create a map to store unique games
      .reduce((unique, game) => {
        // Create a key based on year and round only since these should be unique per game
        const key = `${game.year}_${game.round}`;
        
        // Only store one record per game (either home or away)
        if (!unique.has(key)) {
          unique.set(key, {
            year: game.year,
            round: game.round,
            value: (() => {
              // Map the stat category to the corresponding field
              let value;
              switch(statCategory) {
                case 'all_run_metres':
                  value = game.all_run_metres;
                  break;
                case 'tackles_made':
                  value = game.tackles_made;
                  break;
                case 'performance_points':
                  // Calculate PP for this game
                  let pp = 0;
                  // 4pts per point scored
                  pp += (parseFloat(game['points']) || 0) * 4;
                  // 10pts per try assist
                  pp += (parseFloat(game['try_assists']) || 0) * 10;
                  // 1pt per 10-meter run
                  const runMeters = parseFloat(game['all_run_metres']) || 0;
                  pp += Math.floor(runMeters / 10);
                  // 5pts per line break
                  pp += (parseFloat(game['line_breaks']) || 0) * 5;
                  // 1pt per tackle
                  pp += (parseFloat(game['tackles_made']) || 0);
                  value = pp;
                  break;
                case 'tries':
                  value = game.tries;
                  break;
                case 'points':
                  value = game.points;
                  break;
                case 'line_breaks':
                  value = game.line_breaks;
                  break;
                case 'tackle_breaks':
                  value = game.tackle_breaks;
                  break;
                case 'post_contact_metres':
                  value = game.post_contact_metres;
                  break;
                case 'kick_metres':
                  value = game.kick_metres;
                  break;
                default:
                  value = game[statCategory];
              }
              return value === '-' || value === '' ? 0 : parseFloat(value) || 0;
            })(),
            game: game
          });
        }
        return unique;
      }, new Map());

    return Array.from(playerGames.values())
      .sort((a, b) => {
        // Sort by year first (ascending)
        const yearDiff = parseInt(a.year) - parseInt(b.year);
        if (yearDiff !== 0) return yearDiff;
        
        // Then by round number (ascending)
        const roundA = parseInt(a.round.replace(/\D/g, '')) || 0;
        const roundB = parseInt(b.round.replace(/\D/g, '')) || 0;
        return roundA - roundB;
      });
  }, [players, playerId, statCategory, selectedSeasons]);

  // Apply active filters first
  const filteredStats = sortedStats.filter(stat => {
    const game = stat.game;
    
    // Minutes filter
    const mins = game.mins_played;
    if (!mins || mins === '-') return false;
    const minsValue = parseInt(mins.split(':')[0]) || 0;
    if (minsValue < minsFilter) return false;

    // Check if this is a finals round (rounds 25-28)
    const roundNum = parseInt(stat.round.replace(/\D/g, '')) || 0;
    const isFinalsRound = roundNum >= 25 && roundNum <= 28;

    // Apply finals filter
    if (isFinalsRound && activeFilters.some(filter => filter.type === 'excludeFinals')) {
      return false;
    }

    // Check active filters
    for (const filter of activeFilters) {
      switch (filter.type) {
        case 'home':
          if (!game.is_home_game) return false;
          break;
        case 'away':
          if (game.is_home_game) return false;
          break;
        case 'wins': {
          const isHome = game.is_home_game;
          const homeScore = parseInt(game.home_score);
          const awayScore = parseInt(game.away_score);
          const isWin = isHome ? homeScore > awayScore : awayScore > homeScore;
          if (!isWin) return false;
          break;
        }
        case 'losses': {
          const isHome = game.is_home_game;
          const homeScore = parseInt(game.home_score);
          const awayScore = parseInt(game.away_score);
          const isWin = isHome ? homeScore > awayScore : awayScore > homeScore;
          if (isWin) return false;
          break;
        }
        case 'venue':
          if (game.venue !== filter.value) return false;
          break;
        case 'opposition': {
          // Get the opponent based on whether it's a home or away game
          const opponent = game.is_home_game ? game.away_team : game.home_team;
          if (opponent !== filter.value) return false;
          break;
        }
      }
    }

    return true;
  });

  // Get total available games after filters
  const totalAvailableGames = filteredStats.length;

  // Now apply gameSpan filter (L3/L5/L10) only after other filters are applied
  // This ensures we're getting the last N games of the filtered set
  const gameSpanFilteredStats = gameSpan ? 
    filteredStats.slice(-gameSpan) : 
    filteredStats;

  // Update games displayed when filters change
  React.useEffect(() => {
    // Set games displayed to maximum available when filters change
    setGamesDisplayed(totalAvailableGames);
  }, [totalAvailableGames, minsFilter, locationFilter, resultFilter, gameSpan, activeFilters]);

  // Apply games displayed limit
  sortedStats = gameSpan ? 
    gameSpanFilteredStats : // If gameSpan is set, use those exactly
    filteredStats.slice(-gamesDisplayed); // Otherwise use filteredStats with gamesDisplayed limit

  // Remove or modify the effect that automatically sets the line
  // This effect was overriding the line value from market clicks
  React.useEffect(() => {
    if (filteredStats.length > 0 && line === undefined) {  // Only set if line is undefined
      const sum = filteredStats.reduce((acc, stat) => acc + stat.value, 0);
      const average = Math.round(sum / filteredStats.length);
      // Update the line to the average, minimum of 0.5
      onLineChange(Math.max(average-0.5, 0.5));
    }
  }, [filteredStats, statCategory, onLineChange, line]);  // Add line to dependencies

  // Prepare chart data
  const labels = sortedStats.map(stat => stat.round);
  const homeGames = sortedStats.map(stat => stat.game.is_home);
  const values = sortedStats.map(stat => stat.value);
  const opponents = sortedStats.map(stat => {
    const game = stat.game;
    // Use is_home_game to determine opponent
    return game.is_home_game ? game.away_team : game.home_team;
  });

  // Create image objects for team logos
  const teamLogos = opponents.map(opponent => {
    const img = new Image();
    const normalizedTeam = normalizeTeamNameForLogo(opponent);
    img.src = getTeamLogo(normalizedTeam);
    return img;
  });

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: values.map(value => 
          line !== undefined && value > line 
            ? isDarkMode ? 'rgba(34, 197, 94, 0.8)' : 'rgba(16, 185, 129, 0.8)'
            : isDarkMode ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.8)'
        ),
        borderColor: values.map(value => 
          line !== undefined && value > line 
            ? isDarkMode ? 'rgba(34, 197, 94, 0.8)' : 'rgba(16, 185, 129, 0.8)'
            : isDarkMode ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.8)'
        ),
        borderWidth: 1,
        barThickness: 'flex',
        maxBarThickness: 40,
      },
      line !== undefined ? {
        label: 'Line',
        data: Array(labels.length).fill(line),
        type: 'line',
        borderColor: isDarkMode ? 'rgb(156, 163, 175)' : 'rgb(0, 0, 0)',
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 2,
        order: 0,
      } : null,
    ].filter(Boolean),
  };

  // Update tooltip callbacks
  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top',
        labels: {
          color: isDarkMode ? '#f3f4f6' : '#111827',
          font: {
            size: 12,
            weight: 500
          }
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        titleColor: isDarkMode ? '#f9fafb' : '#111827',
        bodyColor: isDarkMode ? '#f3f4f6' : '#374151',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            const index = items[0].dataIndex;
            const stat = sortedStats[index];
            const game = stat.game;
            const opponent = game.is_home_game ? game.away_team : game.home_team;
            const opponentLogo = getTeamLogo(normalizeTeamName(opponent));
            
            // Create a temporary image element to show the logo
            const tempImg = document.createElement('img');
            tempImg.src = opponentLogo;
            tempImg.style.width = '16px';
            tempImg.style.height = '16px';
            tempImg.style.verticalAlign = 'middle';
            tempImg.style.marginRight = '4px';
            
            return `${stat.round} vs ${opponent} ${game.is_home_game ? '(H)' : '(A)'}`;
          },
          label: (context) => {
            const index = context.dataIndex;
            const game = sortedStats[index].game;
            
            return [
              'Stats:',
              `Run Metres: ${game.all_run_metres || 0}`,
              `Post Contact: ${game.post_contact_metres || 0}`,
              `Tackles Made: ${game.tackles_made || 0}`,
              `Missed Tackles: ${game.missed_tackles || 0}`,
              `Line Breaks: ${game.line_breaks || 0}`,
              `Try Assists: ${game.try_assists || 0}`,
              `Tries: ${game.tries || 0}`,
              `Points: ${game.points || 0}`,
              `Kick Metres: ${game.kick_metres || 0}`,
              `Minutes: ${game.mins_played || '0:00'}`
            ];
          }
        }
      },
      teamLogos: {
        logos: teamLogos
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: isDarkMode ? '#f3f4f6' : '#111827',
          font: {
            weight: 500
          }
        },
        grid: {
          display: false,
        },
        stacked: false,
        afterFit: (scaleInstance) => {
          scaleInstance.paddingTop = 30;
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#f3f4f6' : '#111827',
          font: {
            weight: 500
          },
          callback: function(value, index) {
            let label = labels[index];
            if (homeGames[index]) {
              label += ' 🏠';
            }
            return label;
          }
        },
        stacked: false,
      }
    }
  };

  const handleLineChange = (increment) => {
    const newLine = parseFloat(line) + increment;
    // Ensure line stays within reasonable bounds (e.g., 0 to 100)
    if (newLine >= 0 && newLine <= 100) {
      onLineChange(newLine);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="card h-[400px] relative bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        {/* Season information */}
        <div className="absolute top-2 left-2 flex items-center gap-2 z-10">
          <div className="flex items-center bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm">
            <div className="px-2 py-1 text-xs sm:text-sm font-medium border-r border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100">
              Line: {line}
            </div>
            <div className="flex flex-col">
              <button
                onClick={() => handleLineChange(1)}
                className="px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
              >
                ↑
              </button>
              <button
                onClick={() => handleLineChange(-1)}
                className="px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
              >
                ↓
              </button>
            </div>
          </div>
        </div>

        <Bar data={data} options={options} />
      </div>

      {/* Slider filters below the chart */}
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex flex-row gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <label className="text-[11px] sm:text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block truncate">
              Games ({gamesDisplayed}/{totalAvailableGames})
            </label>
            <div className="hidden sm:flex items-center gap-2">
              <input
                type="range"
                min="5"
                max={totalAvailableGames}
                value={gamesDisplayed}
                onChange={(e) => setGamesDisplayed(Number(e.target.value))}
                className="flex-1 min-w-0 dark:bg-gray-700"
                disabled={totalAvailableGames <= 5}
              />
              <div className="w-10 flex-shrink-0">
                <span className="text-[11px] sm:text-xs font-medium text-gray-900 dark:text-gray-100 block text-right">{gamesDisplayed}</span>
              </div>
            </div>
            <div className="flex sm:hidden items-center justify-between">
              <button 
                onClick={() => setGamesDisplayed(Math.max(5, gamesDisplayed - 1))}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium disabled:opacity-50"
                disabled={gamesDisplayed <= 5}
              >
                -
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="5"
                  max={totalAvailableGames}
                  value={gamesDisplayed}
                  onChange={(e) => setGamesDisplayed(Number(e.target.value))}
                  className="w-12 text-center border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md"
                  disabled={totalAvailableGames <= 5}
                />
              </div>
              <button 
                onClick={() => setGamesDisplayed(Math.min(totalAvailableGames, gamesDisplayed + 1))}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium disabled:opacity-50"
                disabled={gamesDisplayed >= totalAvailableGames}
              >
                +
              </button>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <label className="text-[11px] sm:text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block truncate">
              Min Minutes Played
            </label>
            <div className="hidden sm:flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="80"
                value={minsFilter}
                onChange={(e) => setMinsFilter(Number(e.target.value))}
                className="flex-1 min-w-0 dark:bg-gray-700"
              />
              <div className="w-10 flex-shrink-0">
                <span className="text-[11px] sm:text-xs font-medium text-gray-900 dark:text-gray-100 block text-right">{minsFilter}</span>
              </div>
            </div>
            <div className="flex sm:hidden items-center justify-between">
              <button 
                onClick={() => setMinsFilter(Math.max(0, minsFilter - 5))}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium disabled:opacity-50"
                disabled={minsFilter <= 0}
              >
                -
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="0"
                  max="80"
                  value={minsFilter}
                  onChange={(e) => setMinsFilter(Number(e.target.value))}
                  className="w-12 text-center border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md"
                />
              </div>
              <button 
                onClick={() => setMinsFilter(Math.min(100, minsFilter + 5))}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium disabled:opacity-50"
                disabled={minsFilter >= 100}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatChart; 